import Modal from 'components/Modal';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { selectUser } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { exportDownloadUrl, formatFileSize, isExportInterrupted } from 'utils';
import * as S from './styles';

interface Props {
	onClose: () => void;
}

const formatTimestamp = (iso: string): string => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
};

const DownloadsModal = ({ onClose }: Props) => {
	const { user } = useSelector(
		(r: RootReducer) => ({ user: selectUser(r.db) }),
		shallowEqual
	);

	// Newest first (list is already stored newest-first server-side).
	const recentExports = useMemo(() => user?.metadata?.recentExports ?? [], [user]);

	// Spinner shown between click and the OS save dialog appearing. Cleared by
	// window.blur (the save dialog stealing focus is the signal that the
	// browser→OS handoff completed) or a 5s safety-net timeout.
	const [awaitingSaveDialogId, setAwaitingSaveDialogId] = useState<string | null>(null);
	// Suppresses the unread-dot from flashing back after the spinner clears but before
	// the polling stream reflects the server-side downloadedAt write.
	const [optimisticallyDownloaded, setOptimisticallyDownloaded] = useState<Set<string>>(
		new Set()
	);

	useEffect(() => {
		if (!awaitingSaveDialogId) return;
		const clear = () => setAwaitingSaveDialogId(null);
		const timeoutId = window.setTimeout(clear, 5000);
		window.addEventListener('blur', clear);
		return () => {
			window.clearTimeout(timeoutId);
			window.removeEventListener('blur', clear);
		};
	}, [awaitingSaveDialogId]);

	const onRowClick = (id: string) => {
		setAwaitingSaveDialogId(id);
		setOptimisticallyDownloaded((prev) => {
			const next = new Set(prev);
			next.add(id);
			return next;
		});
	};

	return (
		<Modal title="Downloads" onClose={onClose} onCancel={onClose} cancelLabel="Close">
			<S.Base>
				{recentExports.length === 0 ? (
					<S.Empty>No downloads yet.</S.Empty>
				) : (
					<S.List>
						{recentExports.map((entry) => {
							// Legacy entries (pre-async-export) have no status — treat as ready.
							// A row stuck pending past its driver's lifetime renders as failed.
							const interrupted = isExportInterrupted(entry);
							const status = interrupted ? 'failed' : entry.status ?? 'ready';
							if (status === 'pending') {
								return (
									<S.InertRow key={entry.id}>
										<S.IconWrap>
											<Loader size={18} />
										</S.IconWrap>
										<S.Text>
											<S.Filename>{entry.filename}</S.Filename>
											<S.Meta>
												{entry.label}
												{' · Generating…'}
											</S.Meta>
										</S.Text>
									</S.InertRow>
								);
							}
							if (status === 'failed') {
								return (
									<S.InertRow key={entry.id}>
										<S.IconWrap>
											<Icon type={IconType.ERROR} width={16} height={16} />
										</S.IconWrap>
										<S.Text>
											<S.Filename>{entry.filename}</S.Filename>
											<S.Meta>
												{entry.label}
												{' · '}
												{entry.error || (interrupted ? 'Interrupted' : 'Failed')}
											</S.Meta>
										</S.Text>
									</S.InertRow>
								);
							}
							const isAwaitingSaveDialog = awaitingSaveDialogId === entry.id;
							const isDownloaded =
								!!entry.downloadedAt || optimisticallyDownloaded.has(entry.id);
							return (
								<S.Row
									key={entry.id}
									href={exportDownloadUrl(entry.id)}
									download={entry.filename}
									onClick={() => onRowClick(entry.id)}
								>
									<S.IconWrap>
										{isAwaitingSaveDialog ? (
											<Loader size={18} />
										) : (
											<Icon type={IconType.DOWNLOAD} width={16} height={16} />
										)}
									</S.IconWrap>
									<S.Text>
										<S.Filename>{entry.filename}</S.Filename>
										<S.Meta>
											{entry.label}
											{' · '}
											{formatTimestamp(entry.createdAt)}
											{entry.sizeBytes != null ? ` · ${formatFileSize(entry.sizeBytes)}` : ''}
											{entry.downloadedAt ? '' : ' · not yet downloaded'}
										</S.Meta>
									</S.Text>
									{!isDownloaded && <S.Dot />}
								</S.Row>
							);
						})}
					</S.List>
				)}
			</S.Base>
		</Modal>
	);
};

export default DownloadsModal;
